/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { reduxForm, reset, submit, change } from 'redux-form';
import { changeCarAction, getCarsAction, getRequestInfoAction, resetStateAction } from './actions';
import {
	getCarsOptionsSelector,
	getIsAddCarModalShownSelector,
	getIsAddTrailerModalShownSelector,
	getIsAllowedTrailerSelector,
	getIsButtonsDisabledSelector,
	getIsCarsLoadingSelector,
	getIsModalShownSelector,
	getModalStatusesSelector,
	getReqNameSelector,
	getTrailersOptionsSelector,
	getFormInitialValuesSelector,
	getIsRequestInfoLoadingSelector,
	getIsChangeCarSelector,
	getDriversOptionsSelector,
	getIsCarChangingSelector,
} from './selectors';
import { FORM_NAME } from './constants';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import { CarChangeModalForm } from './components/car-change-modal-form';
import { useInjectSaga } from '../../utils/injectSaga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import saga from './saga';
import { validate } from './utils';
import { CarModal } from './components/car-form';
import { TrailerModal } from './components/trailer-form';
import cancelModal from '../../utils/cancelModal';
import closeModal from '../../utils/closeModal';
import { removeModalQuery } from '../../utils/removeModalQuery';

const CarChangeModalWrapper = ({
	onResetState,
	isModalShown,
	onChangeCar,
	onGetRequestInfo,
	reqName,
	modalStatuses,
	onResetForm,
	isCarsLoading,
	carsOptions,
	onFormSubmit,
	onGetCars,
	isAddCarModalShown,
	isAddTrailerModalShown,
	trailersOptions,
	onFiledChange,
	isAllowedTrailer,
	isRequestInfoLoading,
	isChangeCar,
	driversOptions,
	isCarChanging,
}) => {
	useInjectSaga({ key: 'carChangeModalSaga', saga });
	useInjectReducer({ key: 'carChangeModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		if (isModalShown) {
			onGetRequestInfo(query.get('id'));
			onGetCars(query.get('id'));
		}
	}, [isModalShown]);

	const handleModalCancel = () => {
		cancelModal(query);
		onResetState();
		onResetForm(FORM_NAME);
		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		closeModal(query);
		onResetState();
		onResetForm(FORM_NAME);
		history.push(`${pathname}?${query}`);
	};

	const handleFormSubmit = async () => {
		await onFormSubmit(FORM_NAME);

		onChangeCar({
			redirect: handleModalCancel,
			id: query.get('id'),
			searchQuery: removeModalQuery(query.toString()),
			pathname,
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleFormSubmit();
		}
	};

	const handleCarChange = carId => {
		const trailerId = carsOptions.find(car => car.value === carId)?.trailer_id;

		const trailerAvailable =
			trailerId && trailersOptions.find(trailer => trailer.value === trailerId && !trailer.disabled);

		if (trailerId && trailerAvailable) {
			onFiledChange(FORM_NAME, 'trailer_id', trailerId);
		} else {
			onFiledChange(FORM_NAME, 'trailer_id', 'none');
		}
	};

	if (isAddCarModalShown) {
		return <CarModal />;
	}

	if (isAddTrailerModalShown) {
		return <TrailerModal />;
	}

	return (
		<ModalWindow
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			headerText={reqName}
			modalContent={
				<CarChangeModalForm
					isRequestInfoLoading={isRequestInfoLoading}
					driversOptions={driversOptions}
					isCarsLoading={isCarsLoading}
					carsOptions={carsOptions}
					trailersOptions={trailersOptions}
					onCarChange={handleCarChange}
					isAllowedTrailer={isAllowedTrailer}
				/>
			}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'car_change' }]}
			headerStatuses={modalStatuses}
			footerButtons={
				<ModalFooterButtons
					rightButtons={[
						{
							onClick: handleFormSubmit,
							text: isChangeCar ? 'Заменить' : 'Назначить',
							color: 'primary',
							loading: isCarChanging,
							disabled: isRequestInfoLoading || isCarChanging,
						},
						{
							onClick: handleModalCancel,
							text: 'Отмена',
							color: 'secondary',
							disabled: isCarChanging,
						},
					]}
				/>
			}
		/>
	);
};

const withForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(CarChangeModalWrapper);

const mapStateToProps = createStructuredSelector({
	isModalShown: getIsModalShownSelector(),
	reqName: getReqNameSelector(),
	modalStatuses: getModalStatusesSelector(),
	isButtonsDisabled: getIsButtonsDisabledSelector(),
	isCarsLoading: getIsCarsLoadingSelector(),
	isRequestInfoLoading: getIsRequestInfoLoadingSelector(),
	carsOptions: getCarsOptionsSelector(),
	isAddCarModalShown: getIsAddCarModalShownSelector(),
	isAddTrailerModalShown: getIsAddTrailerModalShownSelector(),
	trailersOptions: getTrailersOptionsSelector(),
	isAllowedTrailer: getIsAllowedTrailerSelector(),
	initialValues: getFormInitialValuesSelector(),
	isChangeCar: getIsChangeCarSelector(),
	driversOptions: getDriversOptionsSelector(),
	isCarChanging: getIsCarChangingSelector(),
});

const mapDispatchToProps = {
	onResetState: resetStateAction,
	onChangeCar: changeCarAction,
	onGetRequestInfo: getRequestInfoAction,
	onResetForm: reset,
	onGetCars: getCarsAction,
	onFormSubmit: submit,
	onFiledChange: change,
};

const CarChangeModal = connect(mapStateToProps, mapDispatchToProps)(withForm);

CarChangeModalWrapper.propTypes = {
	onResetState: PropTypes.func.isRequired,
	isModalShown: PropTypes.bool.isRequired,
	onChangeCar: PropTypes.func.isRequired,
	onGetRequestInfo: PropTypes.func.isRequired,
	reqName: PropTypes.string.isRequired,
	onResetForm: PropTypes.func.isRequired,
	isButtonsDisabled: PropTypes.bool.isRequired,
	isCarsLoading: PropTypes.bool.isRequired,
	carsOptions: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.number.isRequired,
			value: PropTypes.number.isRequired,
			text: PropTypes.string.isRequired,
			trailer_id: PropTypes.number,
		}),
	).isRequired,
	trailersOptions: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			text: PropTypes.string.isRequired,
		}),
	).isRequired,
	driversOptions: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			text: PropTypes.string.isRequired,
		}),
	).isRequired,
	modalStatuses: PropTypes.arrayOf(PropTypes.shape({ color: PropTypes.string, name: PropTypes.string })).isRequired,
	onGetCars: PropTypes.func.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	isAddCarModalShown: PropTypes.bool.isRequired,
	isAddTrailerModalShown: PropTypes.bool.isRequired,
	onFiledChange: PropTypes.func.isRequired,
	isAllowedTrailer: PropTypes.bool.isRequired,
	isRequestInfoLoading: PropTypes.bool.isRequired,
	isChangeCar: PropTypes.bool.isRequired,
	isCarChanging: PropTypes.bool.isRequired,
};

export default CarChangeModal;
